// src/learn/LearnLayout.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { MDXProvider } from '@mdx-js/react';
import { RoddoFishIcon } from '../components/icons';
import { mdxComponents } from './mdxComponents';

type Meta = {
  title?: string;
  description?: string;
  canonical?: string;
  noindex?: boolean;
  updated?: string;
};

function setMetaTag(name: string, content: string) {
  let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setPropertyTag(property: string, content: string) {
  let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(url: string) {
  let el = document.querySelector(`link[rel="canonical"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', url);
}

function readInitialDarkMode() {
  if (typeof window === 'undefined') return false;

  try {
    const stored = window.localStorage.getItem('theme');
    if (stored === 'dark') return true;
    if (stored === 'light') return false;
  } catch {
    // noop
  }

  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
}

export default function LearnLayout({
  children,
  meta,
}: {
  children: React.ReactNode;
  meta?: Meta;
}) {
  const [isDark, setIsDark] = useState(readInitialDarkMode);

  const title = meta?.title ? `${meta.title} · Roddo Learn` : 'Roddo Learn';
  const desc =
    meta?.description ||
    'Calm, practical fishing guides and app tips from Roddo — your pocket fishing buddy.';

  const CANONICAL_ORIGIN = 'https://www.roddo.app';

  const tone = useMemo(
    () => ({
      page: isDark
        ? 'bg-[#120f24] text-[#fffaf0]'
        : 'bg-[#f7efe6] text-[#102033]',
      rail: isDark
        ? 'from-[#171428] via-[#120f24] to-[#0c0a18]'
        : 'from-[#f7efe6] via-[#f6eee5] to-[#ecf6ee]',
      card: isDark
        ? 'border-white/10 bg-[#241f3d]/95 text-[#fffaf0] shadow-[0_12px_0_rgba(0,0,0,0.36)]'
        : 'border-slate-900/10 bg-white/92 text-[#102033] shadow-[0_12px_0_rgba(89,65,37,0.12)]',
      cardSoft: isDark
        ? 'border-white/10 bg-[#2a2447]/92 text-[#fffaf0] shadow-[0_10px_0_rgba(0,0,0,0.30)]'
        : 'border-slate-900/10 bg-white/86 text-[#102033] shadow-[0_10px_0_rgba(89,65,37,0.10)]',
      warning: isDark
        ? 'border-amber-300/20 bg-amber-950/30 text-amber-50 shadow-[0_10px_0_rgba(0,0,0,0.30)]'
        : 'border-amber-500/24 bg-amber-50/95 text-[#3b2a14] shadow-[0_10px_0_rgba(146,96,28,0.12)]',
      chip: isDark
        ? 'border-white/10 bg-white/10 text-white hover:bg-white/15'
        : 'border-slate-900/10 bg-white/88 text-[#102033] hover:bg-white',
      green: isDark
        ? 'border-emerald-300/20 bg-emerald-400/12 text-emerald-100'
        : 'border-emerald-500/20 bg-emerald-50 text-emerald-700',
      muted: isDark ? 'text-white/74' : 'text-slate-700',
    }),
    [isDark],
  );

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem('theme');
      const nextDark = stored === 'dark' || (stored !== 'light' && readInitialDarkMode());
      setIsDark(nextDark);
      document.documentElement.classList.toggle('dark', nextDark);
    } catch {
      // noop
    }
  }, []);

  useEffect(() => {
    const canonical = meta?.canonical || `${CANONICAL_ORIGIN}${window.location.pathname}`;

    document.title = title;
    setMetaTag('description', desc);
    setMetaTag('theme-color', isDark ? '#120f24' : '#f7efe6');

    setPropertyTag('og:title', title);
    setPropertyTag('og:description', desc);
    setPropertyTag('og:type', 'article');
    setPropertyTag('og:url', canonical);
    setPropertyTag('og:image', 'https://www.roddo.app/learn-og.png');

    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', title);
    setMetaTag('twitter:description', desc);
    setMetaTag('twitter:image', 'https://www.roddo.app/learn-og.png');

    setCanonical(canonical);

    let robots = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
    }
    robots.setAttribute('content', meta?.noindex ? 'noindex,nofollow' : 'index,follow');
  }, [title, desc, meta?.noindex, meta?.canonical, isDark]);

  return (
    <div className={`h-[100dvh] min-h-[100dvh] overflow-hidden ${tone.page}`}>
      <main
        className={`h-full overflow-y-auto overscroll-contain bg-gradient-to-b ${tone.rail}`}
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <div className="mx-auto w-full max-w-3xl px-4 pb-24 pt-8 sm:pb-28">
          <div className="mb-6 flex items-center justify-between gap-3">
            <a
              href="/"
              className={`inline-flex min-h-10 items-center justify-center rounded-2xl border px-4 py-2 text-sm font-extrabold transition ${tone.chip}`}
            >
              ← Roddo
            </a>
            <a
              href="/app"
              className="inline-flex min-h-10 items-center justify-center rounded-2xl border border-emerald-700/10 bg-emerald-400 px-5 py-2 text-sm font-extrabold text-emerald-950 shadow-[0_8px_0_rgba(6,78,59,0.22)] transition hover:brightness-105"
            >
              Open App →
            </a>
          </div>

          <section className={`mb-5 rounded-3xl border p-5 ${tone.cardSoft}`}>
            <div className="flex items-center gap-3">
              <div className={`rounded-2xl border p-2 ${tone.chip}`}>
                <RoddoFishIcon
                  mode="png"
                  animated={false}
                  className="block h-7 w-7 scale-x-[1.05] scale-y-[1.80] translate-x-[1px] translate-y-[3px]"
                />
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div className="text-lg font-black leading-tight">Roddo</div>
                <span
                  className={`rounded-full border px-2.5 py-1 text-xs font-black uppercase tracking-[0.18em] ${tone.green}`}
                >
                  Learn
                </span>
                <p className={`w-full text-sm font-semibold leading-5 sm:w-auto sm:pl-1 ${tone.muted}`}>
                  Calm, practical fishing guidance — no hype, no guarantees.
                </p>
              </div>
            </div>
          </section>

          <section className={`mb-6 rounded-3xl border p-4 ${tone.warning}`}>
            <p className="text-sm font-bold leading-6">
              Heads up: regulations vary by location. Always check local rules and conditions.
              Weather, tide, and solunar guidance is informational — no guarantees.
            </p>
          </section>

          <section className={`rounded-3xl border p-6 sm:p-8 ${tone.card}`}>
            <MDXProvider components={mdxComponents}>
              <article className="roddo-learn-prose max-w-none">{children}</article>
            </MDXProvider>
          </section>

          <footer className={`mt-10 flex flex-wrap items-center gap-3 pb-6 text-sm font-semibold ${tone.muted}`}>
            <a className="underline underline-offset-4" href="/learn">
              Learn
            </a>
            <a className="underline underline-offset-4" href="/terms">
              Terms
            </a>
            <a className="underline underline-offset-4" href="/privacy">
              Privacy
            </a>
            <span>© {new Date().getFullYear()} Roddo</span>
          </footer>
        </div>
      </main>
    </div>
  );
}
